import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

/**
 * A guard a Settings panel registers so that navigating away from it — notably
 * switching to a different Settings tab — runs the panel's unsaved-changes check
 * first. It receives the `proceed` closure that performs the navigation and
 * either invokes it immediately (nothing to guard) or defers it behind a
 * confirmation dialog (e.g. the routine editor's Save/Discard prompt).
 */
export type SettingsNavGuard = (proceed: () => void) => void;

export interface SettingsNavGuardContextValue {
  /** Register the active guard, or pass `null` to clear it. */
  setGuard: (guard: SettingsNavGuard | null) => void;
}

const SettingsNavGuardContext = createContext<SettingsNavGuardContextValue | null>(null);

export const SettingsNavGuardProvider = SettingsNavGuardContext.Provider;

/**
 * Consumed by a Settings panel (e.g. the routine editor) to register its
 * unsaved-changes guard. Returns `null` when rendered outside a Settings
 * provider, so the panel keeps working — unguarded — in any standalone context.
 */
export const useSettingsNavGuard = (): SettingsNavGuardContextValue | null =>
  useContext(SettingsNavGuardContext);

/**
 * Registers a nav guard that, while `isDirty`, defers a Settings tab switch
 * behind a confirmation dialog. Returns the dialog's open state plus
 * `confirmLeave` (run the deferred navigation) and `cancelLeave` (stay put).
 * Panels that also offer to *save* (e.g. the routine editor) wire the context
 * directly instead of using this.
 */
export const useSettingsUnsavedGuard = (isDirty: boolean) => {
  const nav = useSettingsNavGuard();
  const pendingRef = useRef<(() => void) | null>(null);
  const [promptingLeave, setPromptingLeave] = useState(false);

  const guard = useCallback<SettingsNavGuard>(
    (proceed) => {
      if (!isDirty) {
        proceed();
        return;
      }
      pendingRef.current = proceed;
      setPromptingLeave(true);
    },
    [isDirty],
  );

  useEffect(() => {
    nav?.setGuard(guard);
    return () => nav?.setGuard(null);
  }, [nav, guard]);

  const confirmLeave = useCallback(() => {
    const proceed = pendingRef.current;
    pendingRef.current = null;
    setPromptingLeave(false);
    proceed?.();
  }, []);

  const cancelLeave = useCallback(() => {
    pendingRef.current = null;
    setPromptingLeave(false);
  }, []);

  return { promptingLeave, confirmLeave, cancelLeave };
};
